import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { useStoreTrajectoryFiltersUrlSync } from 'core/store/useStoreTrajectoryFiltersUrlSync'

import { StoreTrajectoryContext } from './store-trajectory-context'
import { useApiGetStore } from 'core/api/store/useApiGetStore'
import { useGlobalFiltersStore } from 'core/store/useGlobalFiltersStore'
import { useStoreTrajectoryFiltersStore } from 'core/store/useStoreTrajectoryFiltersStore'

interface StoreTrajectoryProviderProps {
  children: ReactNode
}

const StoreTrajectoryProvider = ({ children }: StoreTrajectoryProviderProps) => {
  const storeId = useGlobalFiltersStore(state => state.storeId)
  const targetYear = useStoreTrajectoryFiltersStore(state => state.targetYear)
  const setTargetYear = useStoreTrajectoryFiltersStore(state => state.setTargetYear)
  const referenceStoreId = useStoreTrajectoryFiltersStore(state => state.referenceStoreId)
  const setReferenceStoreId = useStoreTrajectoryFiltersStore(state => state.setReferenceStoreId)

  const [referenceYear, setReferenceYear] = useState<number | null>(null)

  const [forecastInstoreGmv, setForecastInstoreGmv] = useState<number | null>(null)
  const [forecastInstoreMarginRate, setForecastInstoreMarginRate] = useState<number | null>(null)
  const [forecastInstoreSellingPrice, setForecastInstoreSellingPrice] = useState<number | null>(null)
  const [forecastInstoreQuantities, setForecastInstoreQuantities] = useState<number | null>(null)

  const [forecastInstorePhysical, setForecastInstorePhysical] = useState<number | null>(null)
  const [forecastInstoreDigital, setForecastInstoreDigital] = useState<number | null>(null)
  const [forecastInstoreDecapro, setForecastInstoreDecapro] = useState<number | null>(null)
  const [forecastInstoreCircular, setForecastInstoreCircular] = useState<number | null>(null)
  const [forecastInstoreNonCircular, setForecastInstoreNonCircular] = useState<number | null>(null)
  const [forecastInstoreRepair, setForecastInstoreRepair] = useState<number | null>(null)
  const [forecastInstoreSecondLife, setForecastInstoreSecondLife] = useState<number | null>(null)
  const [forecastInstoreBuyBack, setForecastInstoreBuyBack] = useState<number | null>(null)
  const [forecastInstoreRental, setForecastInstoreRental] = useState<number | null>(null)

  const [forecastOutstoreGmv, setForecastOutstoreGmv] = useState<number | null>(null)
  const [forecastOutstoreMarginRate, setForecastOutstoreMarginRate] = useState<number | null>(null)
  const [forecastOutstoreSellingPrice, setForecastOutstoreSellingPrice] = useState<number | null>(null)
  const [forecastOutstoreQuantities, setForecastOutstoreQuantities] = useState<number | null>(null)

  const [typeOfEconomyPercentTotal, setTypeOfEconomyPercentTotal] = useState<number | null>(null)

  const [salesChannelsPercentTotal, setSalesChannelsPercentTotal] = useState<number | null>(null)

  const [circularPercentTotal, setCircularPercentTotal] = useState<number | null>(null)

  const isForecastInstoreGmvValid = !!forecastInstoreGmv && forecastInstoreGmv > 0
  const isForecastInstoreMarginRateValid = !!forecastInstoreMarginRate && forecastInstoreMarginRate > 0
  const isForecastInstoreSellingPriceValid = !!forecastInstoreSellingPrice && forecastInstoreSellingPrice > 0

  const isForecastOutstoreGmvValid = !!forecastOutstoreGmv && forecastOutstoreGmv > 0
  const isForecastOutstoreMarginRateValid = !!forecastOutstoreMarginRate && forecastOutstoreMarginRate > 0
  const isForecastOutstoreSellingPriceValid = !!forecastOutstoreSellingPrice && forecastOutstoreSellingPrice > 0

  const { data: store } = useApiGetStore(storeId)
  const { data: referenceStore } = useApiGetStore(referenceStoreId)

  useStoreTrajectoryFiltersUrlSync()

  useEffect(() => {
    if (forecastInstoreGmv !== null && forecastInstoreSellingPrice !== null) {
      setForecastInstoreQuantities(forecastInstoreGmv / forecastInstoreSellingPrice)
    }
  }, [forecastInstoreGmv, forecastInstoreSellingPrice])

  useEffect(() => {
    if (forecastOutstoreGmv !== null && forecastOutstoreSellingPrice !== null) {
      setForecastOutstoreQuantities(forecastOutstoreGmv / forecastOutstoreSellingPrice)
    }
  }, [forecastOutstoreGmv, forecastOutstoreSellingPrice])

  const toAccountValues = () => {
    return {
      instore: {
        digital_gmv: forecastInstoreDigital,
        physical_gmv: forecastInstorePhysical,
        decapro_gmv: forecastInstoreDecapro,
        eco_circular_repair_and_maintenance_gmv: forecastInstoreRepair,
        eco_circular_saved_products_gmv: forecastInstoreSecondLife,
        eco_circular_buy_back_gmv: forecastInstoreBuyBack,
        eco_circular_rental_gmv: forecastInstoreRental,
        margin_rate: forecastInstoreMarginRate,
        quantity: forecastInstoreQuantities
      },
      outstore: {
        gmv: forecastOutstoreGmv,
        margin_rate: forecastOutstoreMarginRate,
        quantity: forecastOutstoreQuantities
      }
    }
  }

  const isOverAllocated = (percentTotal: number | null) => percentTotal != null && percentTotal > 100

  const areInstoreGmvSegmentsTotalOverAllocated = () => {
    if (!forecastInstoreGmv) {
      return false
    }
    if (isOverAllocated(salesChannelsPercentTotal)) {
      return true
    }
    if (!forecastInstorePhysical) {
      return false
    }
    if (isOverAllocated(typeOfEconomyPercentTotal)) {
      return true
    }
    return !!forecastInstoreCircular && isOverAllocated(circularPercentTotal)
  }

  const isUnderAllocated = (percentTotal: number | null) => percentTotal != null && percentTotal < 100

  const areInstoreGmvSegmentsTotalUnderAllocated = (): boolean => {
    if (!forecastInstoreGmv) {
      return false
    }
    if (isUnderAllocated(salesChannelsPercentTotal)) {
      return true
    }
    if (!forecastInstorePhysical) {
      return false
    }
    if (isUnderAllocated(typeOfEconomyPercentTotal)) {
      return true
    }
    return !!forecastInstoreCircular && isUnderAllocated(circularPercentTotal)
  }

  const areForecastValuesValid = (): boolean => {
    return (
      !areInstoreGmvSegmentsTotalOverAllocated() &&
      !areInstoreGmvSegmentsTotalUnderAllocated() &&
      isForecastInstoreGmvValid &&
      isForecastInstoreMarginRateValid &&
      isForecastInstoreSellingPriceValid &&
      forecastInstorePhysical !== null &&
      forecastInstoreDigital !== null &&
      forecastInstoreDecapro !== null &&
      isForecastOutstoreGmvValid &&
      isForecastOutstoreMarginRateValid &&
      isForecastOutstoreSellingPriceValid
    )
  }

  const contextValue = useMemo(
    () => ({
      storeId,
      store,
      targetYear,
      setTargetYear,
      referenceYear,
      setReferenceYear,
      referenceStoreId,
      setReferenceStoreId,
      referenceStore,
      forecastInstoreGmv,
      setForecastInstoreGmv,
      isForecastInstoreGmvValid,
      forecastInstoreMarginRate,
      setForecastInstoreMarginRate,
      isForecastInstoreMarginRateValid,
      forecastInstoreSellingPrice,
      setForecastInstoreSellingPrice,
      isForecastInstoreSellingPriceValid,
      forecastInstoreQuantities,
      setForecastInstoreQuantities,
      forecastInstorePhysical,
      setForecastInstorePhysical,
      forecastInstoreDigital,
      setForecastInstoreDigital,
      forecastInstoreDecapro,
      setForecastInstoreDecapro,
      forecastInstoreCircular,
      setForecastInstoreCircular,
      forecastInstoreNonCircular,
      setForecastInstoreNonCircular,
      forecastInstoreRepair,
      setForecastInstoreRepair,
      forecastInstoreSecondLife,
      setForecastInstoreSecondLife,
      forecastInstoreBuyBack,
      setForecastInstoreBuyBack,
      forecastInstoreRental,
      setForecastInstoreRental,
      forecastOutstoreGmv,
      setForecastOutstoreGmv,
      isForecastOutstoreGmvValid,
      forecastOutstoreMarginRate,
      setForecastOutstoreMarginRate,
      isForecastOutstoreMarginRateValid,
      forecastOutstoreSellingPrice,
      setForecastOutstoreSellingPrice,
      isForecastOutstoreSellingPriceValid,
      forecastOutstoreQuantities,
      setForecastOutstoreQuantities,
      toAccountValues,
      areForecastValuesValid,
      areInstoreGmvSegmentsTotalUnderAllocated,
      areInstoreGmvSegmentsTotalOverAllocated,
      salesChannelsPercentTotal,
      setSalesChannelsPercentTotal,
      typeOfEconomyPercentTotal,
      setTypeOfEconomyPercentTotal,
      circularPercentTotal,
      setCircularPercentTotal
    }),
    [
      storeId,
      store,
      targetYear,
      setTargetYear,
      forecastInstoreGmv,
      setForecastInstoreGmv,
      isForecastInstoreGmvValid,
      forecastInstoreMarginRate,
      setForecastInstoreMarginRate,
      isForecastInstoreMarginRateValid,
      forecastInstoreSellingPrice,
      setForecastInstoreSellingPrice,
      isForecastInstoreSellingPriceValid,
      forecastInstoreQuantities,
      setForecastInstoreQuantities,
      forecastInstorePhysical,
      setForecastInstorePhysical,
      forecastInstoreDigital,
      setForecastInstoreDigital,
      forecastInstoreDecapro,
      setForecastInstoreDecapro,
      forecastInstoreCircular,
      setForecastInstoreCircular,
      forecastInstoreNonCircular,
      setForecastInstoreNonCircular,
      forecastInstoreRepair,
      setForecastInstoreRepair,
      forecastInstoreSecondLife,
      setForecastInstoreSecondLife,
      forecastInstoreBuyBack,
      setForecastInstoreBuyBack,
      forecastInstoreRental,
      setForecastInstoreRental,
      forecastOutstoreGmv,
      setForecastOutstoreGmv,
      isForecastOutstoreGmvValid,
      forecastOutstoreMarginRate,
      setForecastOutstoreMarginRate,
      isForecastOutstoreMarginRateValid,
      forecastOutstoreSellingPrice,
      setForecastOutstoreSellingPrice,
      isForecastOutstoreSellingPriceValid,
      forecastOutstoreQuantities,
      setForecastOutstoreQuantities,
      toAccountValues,
      areForecastValuesValid,
      areInstoreGmvSegmentsTotalUnderAllocated,
      areInstoreGmvSegmentsTotalOverAllocated,
      salesChannelsPercentTotal,
      setSalesChannelsPercentTotal,
      typeOfEconomyPercentTotal,
      setTypeOfEconomyPercentTotal,
      circularPercentTotal,
      setCircularPercentTotal
    ]
  )

  return <StoreTrajectoryContext.Provider value={contextValue}>{children}</StoreTrajectoryContext.Provider>
}

export default StoreTrajectoryProvider
